"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { ReactNode } from "react";

import { MaintenanceBoundary } from "../maintenance/MaintenanceBoundary";
import type { MaintenanceScreenCopy } from "../maintenance/screen-copy";
import { createMaintenanceStore } from "../maintenance/store";
import { createQueryClient } from "./create-query-client";

export type ApiProviderProps = {
  readonly children: ReactNode;
  /** Pass one in tests or when the app owns it; otherwise one is made and kept for the tree's life. */
  readonly queryClient?: QueryClient;
  /**
   * Draws the full-screen "Back soon" scene when any call is refused for maintenance, with the words to show.
   * It only watches the client the provider makes itself.
   */
  readonly renderMaintenance?: (screen: MaintenanceScreenCopy) => ReactNode;
};

/** Puts the shared data layer (TanStack Query) around an app. */
export function ApiProvider({ children, queryClient, renderMaintenance }: ApiProviderProps) {
  const [maintenance] = useState(createMaintenanceStore);
  const [ownClient] = useState(() => createQueryClient({ onMaintenance: maintenance.report }));
  return (
    <QueryClientProvider client={queryClient ?? ownClient}>
      <MaintenanceBoundary store={maintenance} render={renderMaintenance}>
        {children}
      </MaintenanceBoundary>
    </QueryClientProvider>
  );
}
